import React from 'react';
import { PRODUCT_ADD_ON_IDENTIFIER } from '@thd-olt-functional/utils';
import { formatDate, formatWeek } from './util';

import {
  FULFILLMENT_METHOD_APPLIANCE,
  FULFILLMENT_METHOD_BODFS,
  FULFILLMENT_METHOD_BOPIS,
  FULFILLMENT_METHOD_BOSS,
  FULFILLMENT_METHOD_STH,
  STORE_ASSEMBLY_VALUES
} from './constants';

const {
  HDPP,
  ATTACH_LABOR,
  PRO_ASSEMBLY,
  ATTACH_LABOR_STORE_ASSEMBLY,
  PRO_REFERRAL,
  DUAL_PATH
} = PRODUCT_ADD_ON_IDENTIFIER;

export const getNumberItemsInCart = () => {
  return cookieUtils.getTHDNumberItemsInCart();
};

export const isLoggedIn = () => {
  return !!(typeof window !== 'undefined' && window?.THDCustomer?.default?.isLoggedIn);
};

export const isPaypalEligible = () => {
  // Initializing cookie manager
  if (typeof cookieUtils === 'undefined') {
    return false;
  }
  cookieUtils.initializeCookieManager();
  let isEligible = true;
  if (getNumberItemsInCart() > 0) {
    isEligible = false;
  } else if (isLoggedIn()) {
    isEligible = false;
  }
  return isEligible;
};

export const shouldDisplaySubbuybox = ({ fulfillment, fulfillmentMethod }) => {
  if (fulfillmentMethod === FULFILLMENT_METHOD_BODFS) {
    return false;
  }
  return !!((fulfillment?.fulfillmentOptions || []).length);
};

export const convertFulfillmentConst = ({ cartFulfillmentConst, apiFulfillmentConst }) => {
  if (cartFulfillmentConst) {
    switch (cartFulfillmentConst) {
    case FULFILLMENT_METHOD_STH:
      return 'sth';
    case FULFILLMENT_METHOD_BOSS:
      return 'boss';
    case FULFILLMENT_METHOD_BODFS:
      return 'express delivery';
    default:
      return cartFulfillmentConst.toLowerCase();
    }
  } else if (apiFulfillmentConst) {
    switch (apiFulfillmentConst) {
    case 'sth':
      return FULFILLMENT_METHOD_STH;
    case 'boss':
      return FULFILLMENT_METHOD_BOSS;
    case 'bopis':
      return FULFILLMENT_METHOD_BOPIS;
    case 'express delivery':
      return FULFILLMENT_METHOD_BODFS;
    default:
      return apiFulfillmentConst;
    }
  }
  return null;
};

export const checkInventory = (fulfillmentOptions, apiFulfillmentConst) => {
  if (apiFulfillmentConst === 'express delivery') {
    return fulfillmentOptions.some((fulfillmentOption) => {
      return (fulfillmentOption?.services || []).some((service) => service.type === apiFulfillmentConst);
    });
  }
  return fulfillmentOptions.some((fulfillmentOption) => {
    return (fulfillmentOption?.services || []).some((service) => {
      return service?.type === apiFulfillmentConst
      && (service?.locations || []).some((location) => location?.inventory?.quantity > 0);
    });
  });
};

export const getBogoCartReqParams = (bogoSelectedProduct, { fulfillmentMethod, storeZip, zipCode, storeId }) => {
  let bogoFulfillment = null;
  if (bogoSelectedProduct?.fulfillment?.fulfillmentOptions?.length > 0) {
    const apiFulfillmentMethod = convertFulfillmentConst({ cartFulfillmentConst: fulfillmentMethod });

    bogoFulfillment = checkInventory(bogoSelectedProduct.fulfillment.fulfillmentOptions, apiFulfillmentMethod)
      ? fulfillmentMethod
      : null;
    if (!bogoFulfillment) {
      const fulfillmentsPriority = [
        'sth',
        'bopis',
        'boss',
        'express delivery'
      ];

      bogoFulfillment = fulfillmentsPriority.find((fulfillmentType) => {
        return checkInventory(bogoSelectedProduct.fulfillment.fulfillmentOptions, fulfillmentType);
      });
      bogoFulfillment = convertFulfillmentConst({ apiFulfillmentConst: bogoFulfillment });
    }
    if (bogoFulfillment) {
      /* eslint-disable no-use-before-define */
      return getCartReqParams({
        itemId: bogoSelectedProduct.itemId,
        fulfillmentMethod: bogoFulfillment,
        quantity: 1,
        zipCode,
        storeZip,
        fulfillment: bogoSelectedProduct.fulfillment,
        storeId
      });
    }
  }
  return null;
};

export const getBogoCartOptions = (bogoInfo, { fulfillmentMethod, storeZip, zipCode, storeId }) => {
  if (!bogoInfo) return null;

  const { bogoSelectedProduct, isFreeProduct } = bogoInfo;
  // The "bogo" key in cartOptions tells cart overlay to render a message for a free item
  // but there are some BOGOs where you get a dollar/percent off the second item instead
  const bogo = (isFreeProduct)
    ? { itemDetails: getBogoCartReqParams(bogoSelectedProduct, { fulfillmentMethod, storeZip, zipCode, storeId }) }
    : null;
  const component = bogoSelectedProduct ? 'add both' : null;
  const sharedSection = bogoSelectedProduct ? 'bogo' : null;

  return {
    bogo,
    component,
    sharedSection
  };
};

export const getCartOptions = ({
  channel,
  fulfillmentMethod = '',
  alterBrowserHistory,
  zipCode,
  storeId,
  storeZip,
  bogoInfo
}) => {
  const misship = [
    FULFILLMENT_METHOD_BOPIS,
    FULFILLMENT_METHOD_BOSS
  ].indexOf(fulfillmentMethod) > -1;
  const BODFS = fulfillmentMethod === FULFILLMENT_METHOD_BODFS;
  const directCheckout = !BODFS && fulfillmentMethod !== FULFILLMENT_METHOD_APPLIANCE;

  let host = 'https://www.homedepot.com';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    host = origin.match(/local/g) ? 'https://www.homedepot.com' : origin;
  }
  return {
    host,
    channel,
    misship,
    directCheckout,
    alterBrowserHistory,
    bypassBss: misship,
    bss: misship,
    ...(getBogoCartOptions(bogoInfo, { fulfillmentMethod, storeZip, zipCode, storeId }) || {}),
  };
};

const getNearByStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  if (locations.length > 1) {
    const nearbyStore = locations.find((location) => !location.isAnchor);
    return nearbyStore;
  }
  return null;
};

const getLocalStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  const localStore = locations.find((location) => location.isAnchor);
  return localStore;
};

export const getCartReqParams = ({
  itemId,
  storeZip,
  fulfillmentMethod,
  quantity,
  productAddOns,
  zipCode,
  storeId,
  fulfillment,
  isAssembleItemAtStore,
  bogoInfo
}) => {

  const { warrantyItem = {}, attachedLabor = [], service = {}, subscriptions = {} } = productAddOns || {};
  let cartReqParams = {
    itemId,
    quantity,
    fulfillmentMethod
  };

  // Protection Plan (HDPP) Add on
  if (warrantyItem?.itemId) {
    cartReqParams = {
      ...cartReqParams,
      warrantyItem
    };
  }

  // Attach Labor Add on
  if (attachedLabor.length && attachedLabor[0].attachedLaborSku) {
    cartReqParams = {
      ...cartReqParams,
      attachedLabor
    };
  }

  // Assembly or ProReferral Add on
  if (service?.type) {
    cartReqParams = {
      ...cartReqParams,
      service: {
        type: service.type,
        selected: service.selected
      }
    };
  }

  // subscriptions & Save Add On
  if (subscriptions.frequency) {
    cartReqParams = {
      ...cartReqParams,
      subscriptions
    };
  }

  const pickupService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const bossFulfillment = (pickupService?.services || []).find((store) => store.type === 'boss');
  const bopisFulfillment = (pickupService?.services || []).find((store) => store.type === 'bopis');
  const nearbyStore = getNearByStore(bopisFulfillment);
  let deliveryZipcode = '';

  if (typeof window !== 'undefined') {
    let zipDetails;
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    deliveryZipcode = zipDetails?.zipcode;
  }
  let localStore = { locationId: storeId };
  if (bopisFulfillment) {
    localStore = getLocalStore(bopisFulfillment);
  }
  if (bossFulfillment) {
    localStore = getLocalStore(bossFulfillment);
  }
  switch (fulfillmentMethod) {
  case FULFILLMENT_METHOD_STH:
    cartReqParams.fulfillmentLocation = deliveryZipcode || zipCode;
    break;
  case FULFILLMENT_METHOD_BOSS:
    cartReqParams.fulfillmentLocation = localStore.locationId;
    break;
  case FULFILLMENT_METHOD_BOPIS:
    cartReqParams.fulfillmentLocation = nearbyStore ? nearbyStore.locationId : localStore.locationId;
    break;
  case FULFILLMENT_METHOD_BODFS:
    cartReqParams.fulfillmentLocation = deliveryZipcode || storeZip;
    break;
  case FULFILLMENT_METHOD_APPLIANCE:
    cartReqParams.fulfillmentLocation = zipCode;
    break;
  default:
    break;
  }
  if (isAssembleItemAtStore && fulfillmentMethod !== FULFILLMENT_METHOD_STH) {
    cartReqParams.assembleItemAtStore = STORE_ASSEMBLY_VALUES.SELECTED;
  } else if (isAssembleItemAtStore === false && fulfillmentMethod !== FULFILLMENT_METHOD_STH) {
    cartReqParams.assembleItemAtStore = STORE_ASSEMBLY_VALUES.UN_SELECTED;
  } else {
    delete cartReqParams.assembleItemAtStore;
  }

  if (bogoInfo && bogoInfo.bogoSelectedProduct) {
    const bogoItemReqParams = getBogoCartReqParams(
      bogoInfo.bogoSelectedProduct,
      { fulfillmentMethod, storeZip, zipCode, storeId }
    );
    return [cartReqParams, bogoItemReqParams];
  }

  return cartReqParams;
};

export const useATCLocationDDOStash = ({ stash, itemId }) => {
  const {
    component, pageType, sharedSection, section
  } = stash;

  // stash
  return {
    pageType: pageType || 'pip',
    sharedSection: sharedSection || '',
    section: section || 'buybox',
    component: component || '',
    target: 'atc',
    items: [{
      productID: itemId,
      index: ''
    }]
  };
};

export const renderShippingFulfillment = ({ fulfillment, availabilityType }) => {
  const deliveryService = (fulfillment?.fulfillmentOptions || []).find((option) => option?.type === 'delivery');
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service?.type === 'sth');
  const expressFulfillment = (deliveryService?.services || []).find((service) => service?.type === 'express delivery');
  if (shippingFulfillment) {
    const deliveryStartDate = shippingFulfillment?.deliveryDates?.startDate;
    const deliveryEndDate = shippingFulfillment?.deliveryDates?.endDate;

    const startDate = formatDate(deliveryStartDate);
    const endDate = formatDate(deliveryEndDate);
    const sthTemplate = (
      <>
        {shippingFulfillment.hasFreeShipping && (
          <span className="u__text--success">Free Delivery</span>
        )}
        {!shippingFulfillment.hasFreeShipping
          && shippingFulfillment.freeDeliveryThreshold && (
          <span className="u__text--success">
            {`Free Delivery on $${parseInt(shippingFulfillment.freeDeliveryThreshold, 10)} order`}
          </span>
        )}
        {!shippingFulfillment.hasFreeShipping
          && !shippingFulfillment.freeDeliveryThreshold && (
          <span className="u__text--success">Standard Delivery</span>
        )}
        <span> - </span>
        {startDate && (
          <>
            {startDate === endDate && (
              <>
                <span>Get it by </span>
                <span className="u__bold">
                  {`${formatWeek(deliveryEndDate)}, ${endDate}`}
                </span>
              </>
            )}
            {startDate !== endDate && (
              <>
                <span>Get it </span>
                <span className="u__bold">
                  {startDate}
                </span>
                {endDate && (
                  <span className="u__bold"> - {endDate}</span>
                )}
              </>
            )}
          </>
        )}
      </>
    );
    return {
      method: FULFILLMENT_METHOD_STH,
      showATC: true,
      template: sthTemplate
    };
  }
  if (expressFulfillment) {
    let bodfsTemplate = <span className="u__text--success">Schedule Delivery</span>;
    if (expressFulfillment.deliveryTimeline.toUpperCase() === 'TODAY') {
      bodfsTemplate = (
        <span className="u__text--success">Same-day delivery</span>
      );
    } else if (expressFulfillment.deliveryTimeline.toUpperCase() === 'TOMORROW') {
      bodfsTemplate = (
        <span className="u__text--success">Next-day delivery</span>
      );
    }
    return {
      method: FULFILLMENT_METHOD_BODFS,
      showATC: true,
      template: bodfsTemplate
    };
  }
  if (!fulfillment?.fulfillmentOptions && availabilityType?.type === 'Online') {
    return {
      method: FULFILLMENT_METHOD_STH,
      showATC: false,
      oos: true,
      template: (<span className="u__text--danger">Out of stock online</span>)
    };
  }
  return {
    method: FULFILLMENT_METHOD_STH,
    showATC: false,
    template: (<div> Delivery is <span className="u__text--danger">unavailable</span> for this product </div>)
  };
};

export const getSelectedAddOnInfo = (addOnData = {}, fulfillmentMethod = '') => {
  let addOn = {};
  let flags = {};
  const { checked, addOnType, category } = addOnData;

  switch (addOnType) {
  case HDPP:
    addOn = {
      warrantyItem: {
        itemId: checked ? addOnData.itemId : null
      }
    };
    break;
  case ATTACH_LABOR:
    addOn = {
      attachedLabor: [{
        attachedLaborSku: checked ? addOnData.itemId : null
      }]
    };
    break;
  case ATTACH_LABOR_STORE_ASSEMBLY:
    flags.assembleItemAtStore = fulfillmentMethod === FULFILLMENT_METHOD_BOPIS ? checked : null;
    break;
  case PRO_ASSEMBLY:
    addOn = {
      service: {
        type: checked ? 'Assembly' : null,
        selected: checked,
        category: PRO_ASSEMBLY
      }
    };
    break;
  case PRO_REFERRAL:
    addOn = {
      service: {
        type: checked ? category : null,
        selected: checked,
        category: 'proReferral'
      }
    };
    break;
  case DUAL_PATH:
    addOn = {
      dualPath: checked
    };
    break;
  default:
    addOn = {};
  }
  return { addOn, flags };
};
